import { z } from "zod";
import { caseInsensitivePattern, JSON_SCHEMA_UUID, type JsonSchemaObject } from "./json-schema.ts";

export const AZURE_ACCESS_TARGET_SUBTYPES = ["blob", "fileshare", "container", "secret", "key", "whole", "schema"] as const;
export const AZURE_SCHEMA_NAME_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

const ArmId = z
  .string()
  .trim()
  .min(1)
  .max(1_000)
  .regex(/^\/subscriptions\/[^/]+\/resourceGroups\/[^/]+\/providers\/.+$/i);

export const AzureResourceValue = z.object({ resourceId: ArmId }).strict();
export type AzureResourceValue = z.infer<typeof AzureResourceValue>;

export const AzurePrincipalValue = z.object({ objectId: z.string().uuid() }).strict();
export type AzurePrincipalValue = z.infer<typeof AzurePrincipalValue>;

export const AzureAccessTargetValue = z
  .object({
    resourceId: ArmId,
    accessLevel: z.string().trim().min(1).max(64),
    subtype: z.enum(AZURE_ACCESS_TARGET_SUBTYPES).optional(),
    schema: z.string().regex(AZURE_SCHEMA_NAME_PATTERN).optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if ((value.schema !== undefined) !== (value.subtype === "schema")) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["schema"], message: "schema is required exactly when subtype is schema" });
    }
  });
export type AzureAccessTargetValue = z.infer<typeof AzureAccessTargetValue>;

const ARM_ID_JSON_SCHEMA: JsonSchemaObject = {
  type: "string",
  minLength: 1,
  maxLength: 1_000,
  pattern: `^/${caseInsensitivePattern("subscriptions")}/[^/]+/${caseInsensitivePattern("resourceGroups")}/[^/]+/${caseInsensitivePattern("providers")}/.+$`,
};

export const AZURE_RESOURCE_VALUE_JSON_SCHEMA: JsonSchemaObject = {
  type: "object",
  properties: { resourceId: ARM_ID_JSON_SCHEMA },
  required: ["resourceId"],
  additionalProperties: false,
};

export const AZURE_PRINCIPAL_VALUE_JSON_SCHEMA: JsonSchemaObject = {
  type: "object",
  properties: { objectId: JSON_SCHEMA_UUID },
  required: ["objectId"],
  additionalProperties: false,
};

export const AZURE_ACCESS_TARGET_VALUE_JSON_SCHEMA: JsonSchemaObject = {
  type: "object",
  properties: {
    resourceId: ARM_ID_JSON_SCHEMA,
    accessLevel: { type: "string", minLength: 1, maxLength: 64, pattern: "\\S" },
    subtype: { enum: [...AZURE_ACCESS_TARGET_SUBTYPES] },
    schema: { type: "string", pattern: AZURE_SCHEMA_NAME_PATTERN.source },
  },
  required: ["resourceId", "accessLevel"],
  additionalProperties: false,
  if: { required: ["subtype"], properties: { subtype: { const: "schema" } } },
  // Strict ajv wants `required` names declared alongside.
  // biome-ignore lint/suspicious/noThenProperty: `then` is a JSON Schema keyword, not a thenable.
  then: { required: ["schema"], properties: { schema: {} } },
  else: { not: { required: ["schema"], properties: { schema: {} } } },
};
